import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, LOGO_URL } from "@/lib/api";

const TABS = [
  { id: "manuscripts", label: "Manuscripts" },
  { id: "payments", label: "Payments" },
  { id: "newsletter", label: "Newsletter" },
  { id: "waitlist", label: "Waitlist" },
  { id: "inquiries", label: "Inquiries" },
  { id: "books", label: "Books" },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("manuscripts");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [me, setMe] = useState(null);

  // Session check — cookie is sent automatically via withCredentials.
  useEffect(() => {
    api.get("/admin/me")
      .then((r) => setMe(r.data))
      .catch(() => navigate("/admin/login"));
  }, [navigate]);

  // Load tab data whenever tab changes.
  useEffect(() => {
    setLoading(true);
    setData([]);
    api.get(`/admin/${tab}`)
      .then((r) => setData(r.data))
      .catch((e) => { if (e?.response?.status === 401) navigate("/admin/login"); })
      .finally(() => setLoading(false));
  }, [tab, navigate]);

  const logout = useCallback(async () => {
    try { await api.post("/admin/logout"); } catch { /* ignore */ }
    navigate("/admin/login");
  }, [navigate]);

  const toggleFeatured = useCallback(async (book) => {
    try {
      await api.patch(`/admin/books/${book.id}`, { featured: !book.featured });
      const { data: refreshed } = await api.get("/admin/books");
      setData(refreshed);
      toast.success("Updated.");
    } catch { toast.error("Update failed."); }
  }, []);

  if (!me) return <div className="p-12 text-navy">Loading&hellip;</div>;

  return (
    <div className="min-h-screen bg-white" data-testid="admin-dashboard">
      <DashboardHeader me={me} onLogout={logout} />

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <TabBar tab={tab} setTab={setTab} />
        <DashboardBody tab={tab} data={data} loading={loading} onToggleFeatured={toggleFeatured} />
      </div>
    </div>
  );
}

function DashboardHeader({ me, onLogout }) {
  return (
    <header className="bg-navy text-ivory border-b-2 border-gold">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <img src={LOGO_URL} alt="1123 Publishing Company Logo" className="h-10 w-10 border border-gold/40" />
          <div>
            <div className="font-serif text-xl">Editorial Desk</div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-gold">1123 Publishing &middot; Admin</div>
          </div>
        </Link>
        <div className="flex items-center gap-5">
          <div className="text-sm text-ivory/70 hidden md:block">{me.email}</div>
          <button onClick={onLogout} data-testid="admin-logout"
            className="border border-ivory/40 px-4 py-2 text-[10px] uppercase tracking-[0.24em] hover:bg-gold hover:border-gold hover:text-navy transition-colors">
            Sign out
          </button>
        </div>
      </div>
    </header>
  );
}

function TabBar({ tab, setTab }) {
  return (
    <div className="flex flex-wrap gap-2 mb-8 border-b border-navy/15" data-testid="admin-tabs">
      {TABS.map((t) => (
        <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
          className={`px-5 py-3 text-[11px] uppercase tracking-[0.22em] border-b-2 -mb-px transition-colors ${
            tab === t.id ? "border-gold text-navy" : "border-transparent text-navy/50 hover:text-navy"
          }`}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

function DashboardBody({ tab, data, loading, onToggleFeatured }) {
  if (loading) return <div className="py-20 text-center text-navy/50">Loading&hellip;</div>;
  if (!data.length) return <div className="py-20 text-center text-navy/50">No records yet.</div>;

  if (tab === "manuscripts") return <ManuscriptsTable rows={data} />;
  if (tab === "payments") return <PaymentsTable rows={data} />;
  if (tab === "newsletter") return <NewsletterTable rows={data} />;
  if (tab === "waitlist") return <WaitlistTable rows={data} />;
  if (tab === "inquiries") return <InquiriesTable rows={data} />;
  return <BooksTable rows={data} onToggleFeatured={onToggleFeatured} />;
}

function ManuscriptsTable({ rows }) {
  return (
    <Table
      headers={["Date", "Author", "Title", "Genre", "Email", "File", "Status"]}
      rows={rows.map((m) => ({
        key: m.id,
        cells: [
          new Date(m.created_at).toLocaleDateString(),
          m.author_name,
          m.title,
          m.genre,
          m.email,
          m.file ? <ManuscriptFileLink key={`${m.id}-file`} ms={m} /> : <span className="text-navy/30">—</span>,
          m.status,
        ],
      }))}
      testid="manuscripts-table"
    />
  );
}

function ManuscriptFileLink({ ms }) {
  const download = async () => {
    try {
      const res = await api.get(`/admin/manuscripts/${ms.id}/file`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: ms.file?.content_type || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = ms.file?.original_name || "manuscript";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download file.");
    }
  };
  const sizeMb = ms.file?.size_bytes ? (ms.file.size_bytes / (1024 * 1024)).toFixed(1) : null;
  return (
    <button
      type="button"
      onClick={download}
      data-testid={`ms-download-${ms.id}`}
      className="text-xs uppercase tracking-[0.2em] text-navy hover:text-gold underline underline-offset-4"
    >
      {ms.file?.original_name?.slice(0, 22) || "Download"}{sizeMb ? ` · ${sizeMb}MB` : ""}
    </button>
  );
}

function PaymentsTable({ rows }) {
  return (
    <Table
      headers={["Date", "Type", "Amount", "Status", "Customer", "Session"]}
      rows={rows.map((p) => ({
        key: p.id || p.session_id,
        cells: [
          new Date(p.created_at).toLocaleDateString(),
          p.type, `$${Number(p.amount).toFixed(2)}`,
          p.payment_status, p.metadata?.customer_email || p.metadata?.donor_email || "—",
          (p.session_id || "").slice(0, 14) + "…",
        ],
      }))}
      testid="payments-table"
    />
  );
}

function NewsletterTable({ rows }) {
  return (
    <Table
      headers={["Date", "Email", "Name"]}
      rows={rows.map((n) => ({
        key: n.id || n.email,
        cells: [new Date(n.created_at).toLocaleDateString(), n.email, n.name || "—"],
      }))}
      testid="newsletter-table"
    />
  );
}

function WaitlistTable({ rows }) {
  return (
    <Table
      headers={["Date", "Book", "Name", "Email"]}
      rows={rows.map((w) => ({
        key: w.id || `${w.book_slug}-${w.email}`,
        cells: [new Date(w.created_at).toLocaleDateString(), w.book_title, w.name || "—", w.email],
      }))}
      testid="waitlist-table"
    />
  );
}

function InquiriesTable({ rows }) {
  return (
    <Table
      headers={["Date", "Name", "Service", "Email", "Summary"]}
      rows={rows.map((i) => ({
        key: i.id || `${i.email}-${i.created_at}`,
        cells: [
          new Date(i.created_at).toLocaleDateString(),
          i.name, i.service, i.email, (i.project_summary || "").slice(0, 80),
        ],
      }))}
      testid="inquiries-table"
    />
  );
}

function BooksTable({ rows, onToggleFeatured }) {
  const priced = useMemo(
    () => rows.filter((b) => b && typeof b.price === "number"),
    [rows]
  );
  return (
    <div className="overflow-x-auto border border-navy/15 bg-white">
      <table className="w-full text-sm">
        <thead className="bg-navy text-ivory">
          <tr>{["Title", "Author", "Category", "Price", "Featured", ""].map((h) => (
            <th key={h} className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.22em] font-medium">{h}</th>
          ))}</tr>
        </thead>
        <tbody>
          {priced.map((b) => (
            <tr key={b.id} className="border-t border-navy/10 hover:bg-parchment">
              <td className="px-4 py-3 font-serif text-navy">{b.title}</td>
              <td className="px-4 py-3 text-charcoal/80">{b.author}</td>
              <td className="px-4 py-3 text-charcoal/80 uppercase text-xs tracking-wider">{b.category}</td>
              <td className="px-4 py-3 text-navy font-serif">${Number(b.price).toFixed(2)}</td>
              <td className="px-4 py-3">{b.featured ? <span className="text-gold">&#9733; Featured</span> : <span className="text-navy/40">—</span>}</td>
              <td className="px-4 py-3 text-right">
                <button onClick={() => onToggleFeatured(b)} data-testid={`toggle-${b.slug}`}
                  className="text-xs uppercase tracking-[0.2em] border border-navy/30 px-3 py-1.5 hover:bg-navy hover:text-ivory transition-colors">
                  {b.featured ? "Unfeature" : "Feature"}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Generic table — rows use stable string keys, never array indexes.
function Table({ headers, rows, testid }) {
  return (
    <div className="overflow-x-auto border border-navy/15 bg-white" data-testid={testid}>
      <table className="w-full text-sm">
        <thead className="bg-navy text-ivory">
          <tr>{headers.map((h) => (
            <th key={h} className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.22em] font-medium">{h}</th>
          ))}</tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key} className="border-t border-navy/10 hover:bg-parchment">
              {row.cells.map((c, j) => (
                <td key={`${row.key}-c${j}`} className="px-4 py-3 text-charcoal/85">{c}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
